/**
 * expenseSummary — Pure aggregation for the Vorsteuer-Cockpit (M8).
 *
 * Aggregates the already-loaded `expenses` array (and optional income) into the
 * four cockpit key figures, sign-aware and in integer cents. This module is
 * intentionally PURE: it imports NO Firebase and NO React — only the `Expense`
 * TYPE (erased at compile, so no runtime firebase dependency). That keeps it
 * trivially unit-testable in isolation.
 *
 * Cent-Disziplin (golden rule): never `centsToEuros` BEFORE summation. Summation
 * happens entirely on integer cents; formatting happens once at the render edge
 * in the component. All sums are sign-aware — a credit note (EN16931 type 381)
 * carries NEGATIVE cents and reduces the period sums automatically (§17 UStG),
 * with NO special-casing on `documentTypeCode`.
 *
 * Fail-closed inclusion rules (§2.3 of architecture-vorsteuer-cockpit.md):
 *   - Official sums count only `status === 'booked'` (drafts are previews).
 *   - "Abziehbare Vorsteuer" additionally requires `completenessStatus === 'complete'`
 *     (defensive safety belt G-2 — "deduct too little" is the safe direction, §15 UStG).
 *   - "Geschuldete USt" (§13b) is NEVER filtered by completeness — declaration duty
 *     arises by law (§13b UStG). The only permitted filter on vatOwedCents is `status`.
 *   - A1: drafts carrying a §13b/IGL liability are surfaced as `excludedDraftVatOwedCount`
 *     so a real-but-unbooked tax debt never silently disappears (fail-open guard).
 *
 * @module expenseSummary
 */

import type { Expense, TaxScheme } from '../types/expenseTypes';

// ─── Period types ───────────────────────────────────────────────────────────────

/** Granularität des Zeitraum-Filters. */
export type SummaryPeriodKind = 'month' | 'quarter' | 'year';

/** Aufgelöster Zeitraum als ISO-Bounds (inklusive Grenzen, YYYY-MM-DD). */
export interface SummaryPeriod {
  kind: SummaryPeriodKind;
  /** ISO YYYY-MM-DD, inklusive untere Grenze. */
  fromISO: string;
  /** ISO YYYY-MM-DD, inklusive obere Grenze. */
  toISO: string;
  /**
   * Roh-Anzeige-Label (z. B. "Q1 2026" / "2026-03" / "2026"). Die finale
   * Lokalisierung passiert in der Komponente über year/quarter/month + kind.
   */
  labelKey: string;
  year: number;
  /** 1..4 wenn kind==='quarter'. */
  quarter?: number;
  /** 1..12 wenn kind==='month'. */
  month?: number;
}

/** Eingabe-Form für eine Einnahme (entkoppelt von der Firestore-InvoiceDoc-Form). */
export interface IncomeEntry {
  /** Periodendatum (Rechnungsdatum), ISO YYYY-MM-DD. */
  dateISO: string;
  /** BRUTTO-Gesamtbetrag in Integer-Cents (vorzeichen-aware). */
  grossCents: number;
}

/** Ergebnis der Aggregation. ALLE Beträge Integer-Cents, vorzeichen-aware. */
export interface ExpenseSummary {
  period: SummaryPeriod;
  /** Σ netCents der einbezogenen Ausgaben. */
  expenseNetCents: number;
  /** Σ vatDeductibleCents (= „Abziehbare Vorsteuer"). */
  vatDeductibleCents: number;
  /** Σ vatOwedCents (= „Geschuldete USt", §13b/IGL). */
  vatOwedCents: number;
  /** Σ grossCents der einbezogenen Ausgaben (für Brutto-Saldo + Anzeige). */
  expenseGrossCents: number;
  /** Anzahl der in die offiziellen Summen einbezogenen Ausgaben. */
  includedExpenseCount: number;
  /**
   * A1: Anzahl der status==='draft'-Ausgaben IN DER PERIODE, die vatOwedCents!==0
   * tragen ODER (taxScheme ∈ {reverse_charge_13b, intra_community_6a} UND
   * taxSchemeConfirmedByUser===false). >0 ⇒ UI rendert cockpit.vatOwedDraftHint.
   */
  excludedDraftVatOwedCount: number;
  /**
   * „Einnahmen − Ausgaben (brutto)" (BRUTTO/Soll) = incomeGrossCents - expenseGrossCents;
   * null wenn Einnahmen nicht verfügbar. KEINE EÜR (A2) — interner Symbolname,
   * der NIE als sichtbarer Text gerendert wird.
   */
  euerBalanceCents: number | null;
  /** Σ Einnahmen-Brutto der Periode; null wenn Einnahmen-Quelle deaktiviert/fehlgeschlagen. */
  incomeGrossCents: number | null;
}

/** Optionen steuern die Einbezugsregeln (§2.3) — defaults fail-closed. */
export interface SummarizeOptions {
  /** Default true: nur status==='booked' zählt in die offiziellen Summen. */
  bookedOnly?: boolean;
  /** Default true: Vorsteuer nur bei completenessStatus==='complete'. */
  requireCompleteForDeductible?: boolean;
}

// ─── Period helpers (pure) ──────────────────────────────────────────────────────

/** §13b/IGL-Schemata, deren USt der Empfänger kraft Gesetzes schuldet. */
const REVERSE_CHARGE_SCHEMES: ReadonlySet<TaxScheme> = new Set<TaxScheme>([
  'reverse_charge_13b',
  'intra_community_6a',
]);

/** Zwei-stellige Null-gepaddete Zahl als String. */
function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** Letzter Tag des Monats (month 1..12) — ohne ISO-Parsing/Zeitzonen-Falle. */
function lastDayOfMonth(year: number, month: number): number {
  // Date(year, month, 0) → Tag 0 des Folgemonats = letzter Tag von `month`.
  return new Date(year, month, 0).getDate();
}

/**
 * Löst einen Zeitraum aus Granularität + Referenzdatum (heute) auf.
 *
 * @param kind  'month' | 'quarter' | 'year'
 * @param refISO Referenzdatum ISO YYYY-MM-DD (i. d. R. heute). Erste 10 Zeichen zählen.
 * @returns SummaryPeriod mit inklusiven ISO-Grenzen und Anzeige-Bestandteilen.
 */
export function resolvePeriod(kind: SummaryPeriodKind, refISO: string): SummaryPeriod {
  const year = Number.parseInt(refISO.slice(0, 4), 10);
  const month = Number.parseInt(refISO.slice(5, 7), 10);

  if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12) {
    throw new Error(`resolvePeriod: invalid refISO '${refISO}'`);
  }

  if (kind === 'year') {
    return {
      kind,
      fromISO: `${year}-01-01`,
      toISO: `${year}-12-31`,
      labelKey: `${year}`,
      year,
    };
  }

  if (kind === 'quarter') {
    const quarter = Math.ceil(month / 3); // 1..4
    const fromMonth = (quarter - 1) * 3 + 1;
    const toMonth = quarter * 3;
    return {
      kind,
      fromISO: `${year}-${pad2(fromMonth)}-01`,
      toISO: `${year}-${pad2(toMonth)}-${pad2(lastDayOfMonth(year, toMonth))}`,
      labelKey: `Q${quarter} ${year}`,
      year,
      quarter,
    };
  }

  // month
  return {
    kind,
    fromISO: `${year}-${pad2(month)}-01`,
    toISO: `${year}-${pad2(month)}-${pad2(lastDayOfMonth(year, month))}`,
    labelKey: `${year}-${pad2(month)}`,
    year,
    month,
  };
}

/** Default-Periode: laufendes Quartal aus dem Referenzdatum. */
export function currentQuarterPeriod(refISO: string): SummaryPeriod {
  return resolvePeriod('quarter', refISO);
}

/**
 * Liegt `dateISO` (inklusive) innerhalb des Zeitraums?
 * Lexikografischer Vergleich auf den ersten 10 ISO-Zeichen — keine Date-Zeitzonen-Falle.
 *
 * @returns true wenn from <= d <= to.
 */
export function isWithinPeriod(dateISO: string, period: SummaryPeriod): boolean {
  if (!dateISO) return false;
  const d = dateISO.slice(0, 10);
  return d >= period.fromISO && d <= period.toISO;
}

// ─── Aggregation (pure) ─────────────────────────────────────────────────────────

/**
 * Reine, vorzeichen-aware Aggregation der Ausgaben (+ optional Einnahmen) für eine Periode.
 *
 * Total (wirft nie): leere/teilweise Daten liefern Cent-Summen 0. Alle Ergebnis-Cents
 * sind per Konstruktion Integer (reine Integer-Arithmetik über Integer-Eingaben).
 *
 * @param expenses Bereits geladenes Ausgaben-Array (dasselbe wie die Tabelle).
 * @param period   Aufgelöster Zeitraum (resolvePeriod / currentQuarterPeriod).
 * @param income   Einnahmen für den Brutto-Saldo; null ⇒ euerBalanceCents/incomeGrossCents=null.
 * @param options  Einbezugsregeln (defaults fail-closed: bookedOnly + requireCompleteForDeductible).
 * @returns ExpenseSummary mit allen Kennzahlen.
 */
export function summarizeExpenses(
  expenses: Expense[],
  period: SummaryPeriod,
  income: IncomeEntry[] | null,
  options?: SummarizeOptions,
): ExpenseSummary {
  const bookedOnly = options?.bookedOnly ?? true;
  const requireCompleteForDeductible = options?.requireCompleteForDeductible ?? true;

  let expenseNetCents = 0;
  let vatDeductibleCents = 0;
  let vatOwedCents = 0;
  let expenseGrossCents = 0;
  let includedExpenseCount = 0;
  let excludedDraftVatOwedCount = 0;

  for (const e of expenses ?? []) {
    if (!isWithinPeriod(e.expenseDate, period)) continue;

    const isBooked = e.status === 'booked';

    // A1: draft-Schulden in der Periode sichtbar machen (vor dem booked-Gate).
    if (e.status === 'draft') {
      const carriesOwed = e.vatOwedCents !== 0;
      const unconfirmedReverseCharge =
        REVERSE_CHARGE_SCHEMES.has(e.taxScheme) && e.taxSchemeConfirmedByUser === false;
      if (carriesOwed || unconfirmedReverseCharge) {
        excludedDraftVatOwedCount += 1;
      }
    }

    // Offizielle Summen: nur einbezogene Ausgaben (fail-closed: booked).
    if (bookedOnly && !isBooked) continue;

    includedExpenseCount += 1;
    expenseNetCents += e.netCents;
    expenseGrossCents += e.grossCents;

    // Geschuldete USt: NIE nach completeness filtern (fail-open-Schutz §13b).
    vatOwedCents += e.vatOwedCents;

    // Abziehbare Vorsteuer: zusätzlich nur bei completenessStatus==='complete' (G-2).
    const deductibleAllowed = !requireCompleteForDeductible || e.completenessStatus === 'complete';
    if (deductibleAllowed) {
      vatDeductibleCents += e.vatDeductibleCents;
    }
  }

  // Einnahmen-Brutto (Soll-Basis) für den Brutto-Saldo.
  let incomeGrossCents: number | null = null;
  let euerBalanceCents: number | null = null;
  if (income !== null) {
    incomeGrossCents = 0;
    for (const inc of income) {
      if (!isWithinPeriod(inc.dateISO, period)) continue;
      incomeGrossCents += inc.grossCents;
    }
    euerBalanceCents = incomeGrossCents - expenseGrossCents;
  }

  return {
    period,
    expenseNetCents,
    vatDeductibleCents,
    vatOwedCents,
    expenseGrossCents,
    includedExpenseCount,
    excludedDraftVatOwedCount,
    euerBalanceCents,
    incomeGrossCents,
  };
}
